import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Stack,
  Image,
  Loader,
  Stepper,
  Text,
  Radio,
  RadioGroup,
  Checkbox,
  Title,
  Notification,
  Paper,
  Divider,
  SimpleGrid,
} from "@mantine/core";
import { InfoCircle } from "tabler-icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, functions, db } from "../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { createUserWithEmailAndPassword, sendEmailVerification, EmailAuthProvider, linkWithCredential, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { getDocs, query, collection, where } from "firebase/firestore";
import QRCode from "qrcode";
import { RequestOnboardingHelpModal } from "../components/modals/request-onboarding-help-modal";
import "./app.css";

export function clearOnboardingSession(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith("captiva_onboarding_")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch (e) {}
}

const SAFE_DRAFT_ID_REGEX = /^[A-Za-z0-9_-]{1,128}$/;

export function sanitizeDraftId(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return SAFE_DRAFT_ID_REGEX.test(trimmed) ? trimmed : null;
}

export interface DraftedModuleConfig {
  moduleName?: string;
  forms?: Array<{ id?: string; name?: string; fieldsCount?: number; [key: string]: unknown }>;
  reports?: Array<{ id?: string; name?: string; [key: string]: unknown }>;
  entities?: Array<{ kind?: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

export type CaptivaAppKey = "fuel" | "flex" | "trip";

export const REGISTRATION_APPS: Record<
  CaptivaAppKey,
  { label: string; scheme: string; iosUrl: string; androidUrl: string }
> = {
  fuel: {
    label: "Captiva Fuel",
    scheme: "captivafuel",
    iosUrl: "https://apps.apple.com/us/app/captiva-fuel/id6480343590",
    androidUrl: "https://play.google.com/store/apps/details?id=com.captiva.fuel",
  },
  flex: {
    label: "Captiva Flex",
    scheme: "captivaflex",
    iosUrl: "https://apps.apple.com/us/app/captiva-flex/id6752111609",
    androidUrl: "https://play.google.com/store/apps/details?id=com.captiva.flex",
  },
  trip: {
    label: "Captiva Trip",
    scheme: "captivatrip",
    iosUrl: "https://apps.apple.com/us/app/captiva-trip/id6740551915",
    androidUrl: "https://play.google.com/store/apps/details?id=com.captiva.trip",
  },
};

export function buildMobileAppContinueUrl(
  appKey: CaptivaAppKey,
  nonce?: string | null
): string {
  const scheme = REGISTRATION_APPS[appKey].scheme;
  if (nonce && nonce.trim()) {
    return `${scheme}://l?nonce=${encodeURIComponent(nonce.trim())}`;
  }
  return `${scheme}://login`;
}

export function Register() {
  const [active, setActive] = useState(0);
  const [loadingStep, setLoadingStep] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [installedModule, setInstalledModule] = useState<DraftedModuleConfig | null>(null);
  const [helpModalOpened, setHelpModalOpened] = useState(false);
  const [loginNonce, setLoginNonce] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordCheck, setPasswordCheck] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [workspaceMode, setWorkspaceMode] = useState("createNew"); // createNew or joinExisting
  const [companyName, setCompanyName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [town, setTown] = useState("");
  const [province, setProvince] = useState("");
  const [country, setCountry] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const [activationCode, setActivationCode] = useState("");

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isFresh = searchParams.get("fresh") === "1";
  const queryDraftId = sanitizeDraftId(searchParams.get("draftId") || searchParams.get("token"));
  const rawAppParam = (searchParams.get("app") || "").trim().toLowerCase();
  const sourceApp: CaptivaAppKey | null =
    rawAppParam === "fuel" || rawAppParam === "flex" || rawAppParam === "trip"
      ? rawAppParam
      : null;

  // Step 3 App Selection State
  const [selectedApps, setSelectedApps] = useState<{
    fuel: boolean;
    flex: boolean;
    trip: boolean;
  }>({
    fuel: true,
    flex: true,
    trip: sourceApp === "trip",
  });

  const [user, loading] = useAuthState(auth);
  
  const [isClaiming, setIsClaiming] = useState(false);
  const authCheckedRef = useRef(false);

  useEffect(() => {
    if (isFresh) {
      clearOnboardingSession();
    } else if (queryDraftId) {
      sessionStorage.setItem("captiva_onboarding_draft_id", queryDraftId);
    }
    const rawSessionDraft = sessionStorage.getItem("captiva_onboarding_draft_id");
    const sessionDraftId = sanitizeDraftId(rawSessionDraft);
    if (rawSessionDraft && !sessionDraftId) {
      sessionStorage.removeItem("captiva_onboarding_draft_id");
    }
    if (sessionDraftId || (queryDraftId && !isFresh)) {
      setIsClaiming(true);
      setWorkspaceMode("createNew");
    } else {
      setIsClaiming(false);
    }
  }, [user, queryDraftId, isFresh]);

  useEffect(() => {
    if (isFresh) return;
    if (authCheckedRef.current) return;
    if (!user || user.isAnonymous) return;
    if (active !== 0) return;

    authCheckedRef.current = true;
    let isMounted = true;

    (async () => {
      try {
        let companyId = "";
        let isOperator = false;
        if (typeof user.getIdTokenResult === "function") {
          const tokenResult = await user.getIdTokenResult();
          companyId = (tokenResult?.claims?.companyId as string) || "";
          isOperator = tokenResult?.claims?.role === "operator" || tokenResult?.claims?.admin === false;
        }

        const activeDraftId = queryDraftId || sanitizeDraftId(sessionStorage.getItem("captiva_onboarding_draft_id"));

        if (companyId) {
          if (activeDraftId && !isOperator) {
            setLoadingStep(true);
            const installDraftedModule = httpsCallable(functions, "installDraftedModule");
            const installResult = await installDraftedModule({
              claimToken: activeDraftId,
              companyId,
            });
            sessionStorage.removeItem("captiva_onboarding_draft_id");
            const installedConfig = (installResult.data as any)?.moduleConfig;
            const appList: string[] = [];
            if (installedConfig?.forms && installedConfig.forms.length > 0) appList.push("flex");
            if (installedConfig?.entities?.some((e: any) => e.kind === "vehicle" || e.kind === "tank" || e.kind === "fuelType")) appList.push("fuel");
            if (installedConfig?.entities?.some((e: any) => e.kind === "vehicle" || e.kind === "vehicleGroup")) appList.push("trip");
            const appsQuery = appList.length > 0 ? `&apps=${encodeURIComponent(appList.join(","))}` : "";
            window.location.href = `/build/onboard/success?token=${encodeURIComponent(activeDraftId)}${appsQuery}`;
            return;
          } else {
            if (activeDraftId) {
              sessionStorage.removeItem("captiva_onboarding_draft_id");
            }
            navigate("/dashboard/overview");
            return;
          }
        }

        // Authenticated without companyId: check existing user profile document
        if (user.emailVerified === false && typeof user.reload === "function") {
          try {
            await user.reload();
          } catch {
            // ignore reload error
          }
        }
        if (user.emailVerified === false && auth.currentUser?.emailVerified === false) {
          if (isMounted) setActive(1);
          return;
        }

        const q = query(collection(db, "users"), where("uid", "==", user.uid));
        const userSnap = await getDocs(q);
        if (!isMounted) return;

        if (!userSnap.empty) {
          const userData = userSnap.docs[0].data();
          if (userData.firstName) setFirstName(userData.firstName);
          if (userData.lastName) setLastName(userData.lastName);
          if (userData.firstName && userData.lastName) {
            setActive(3);
            return;
          }
        }
        setActive(2);
      } catch (err: any) {
        console.error("Error checking user profile on register screen:", err);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [user, queryDraftId, active, navigate, isFresh]);

  useEffect(() => {
    if (isFresh) {
      clearOnboardingSession();
      if (auth.currentUser || user) {
        signOut(auth).catch(console.error);
      }
      authCheckedRef.current = false;
      setActive(0);
      const params = new URLSearchParams(searchParams);
      params.delete("fresh");
      setSearchParams(params, { replace: true });
    }
  }, [isFresh, user, searchParams, setSearchParams]);

  const handleUseDifferentEmail = async () => {
    clearOnboardingSession();
    setEmail("");
    setPassword("");
    setPasswordCheck("");
    setTermsAccepted(false);
    setFirstName("");
    setLastName("");
    setCompanyName("");
    setAddressLine1("");
    setTown("");
    setProvince("");
    setCountry("");
    setPostalCode("");
    setPhoneNumber("");
    setActivationCode("");
    setErrorMessage("");
    authCheckedRef.current = false;
    setActive(0);
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Sign out failed:", err);
    }
  };

  const handleAccountCreation = async () => {
    setErrorMessage("");
    if (password !== passwordCheck) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters.");
      return;
    }
    setLoadingStep(true);
    try {
      let userCredential;
      if (user && user.isAnonymous) {
        const credential = EmailAuthProvider.credential(email, password);
        userCredential = await linkWithCredential(user, credential);
      } else {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
      }
      await sendEmailVerification(userCredential.user, {
        url: `${window.location?.origin || "https://captivadata.app"}/register`,
      });
      setActive(1);
    } catch (err: any) {
      if (err.code === "auth/email-already-in-use" || err.message?.includes("email-already-in-use")) {
        try {
          await signInWithEmailAndPassword(auth, email, password);
          authCheckedRef.current = false;
          return;
        } catch (loginErr: any) {
          setErrorMessage(
            "An account with this email already exists. Please verify your password or log in to link your workspace."
          );
          return;
        }
      }
      setErrorMessage(err.message || "Account creation failed.");
    } finally {
      setLoadingStep(false);
    }
  };

  const checkAndAdvanceEmailVerified = async (showErrorIfUnverified: boolean): Promise<boolean> => {
    try {
      await auth.currentUser?.reload();
    } catch {
      // ignore reload error and try token refresh fallback
    }
    if (auth.currentUser?.emailVerified) {
      setErrorMessage("");
      setActive(2);
      return true;
    }
    if (typeof auth.currentUser?.getIdTokenResult === "function") {
      try {
        const tokenResult = await auth.currentUser.getIdTokenResult(true);
        if (auth.currentUser?.emailVerified || tokenResult?.claims?.email_verified === true) {
          setErrorMessage("");
          setActive(2);
          return true;
        }
      } catch {
        // ignore token refresh error
      }
    }
    if (showErrorIfUnverified) {
      setErrorMessage("Email is not verified yet. Please check your inbox.");
    }
    return false;
  };

  const handleCheckEmailVerified = async () => {
    setErrorMessage("");
    setLoadingStep(true);
    try {
      await checkAndAdvanceEmailVerified(true);
    } catch (err: any) {
      setErrorMessage(err?.message || "Could not verify email status. Please try again.");
    } finally {
      setLoadingStep(false);
    }
  };

  useEffect(() => {
    if (active !== 1) return;
    let cancelled = false;
    const pollVerification = async () => {
      if (cancelled) return;
      await checkAndAdvanceEmailVerified(false);
    };
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") {
        pollVerification();
      }
    };
    window.addEventListener("focus", pollVerification);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    const intervalId = window.setInterval(pollVerification, 3000);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", pollVerification);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.clearInterval(intervalId);
    };
  }, [active]);

  const handleProfileSubmit = async () => {
    if (!firstName || !lastName) {
      setErrorMessage("Please provide your first and last name.");
      return;
    }
    setErrorMessage("");
    setLoadingStep(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not authenticated");
      const token = await currentUser.getIdToken();

      const baseUrl = process.env.REACT_APP_APP_ENV === 'local' 
        ? 'http://127.0.0.1:5001/twodata-dev/europe-west3/tenantapi'
        : 'https://europe-west3-twodata-dev.cloudfunctions.net/tenantapi';

      const payload = {
        firstName,
        lastName,
        isActive: true,
        nextOnboardingStep: "workspace",
        uid: currentUser.uid,
      };

      const resp = await fetch(`${baseUrl}/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_X_API_KEY || "0f247291-b4bb-4895-91ac-ba26af7e55a2",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const body = await resp.json();
        throw new Error(body.message || "Failed to create user profile");
      }

      setActive(3);
    } catch (err: any) {
      setErrorMessage(err.message || "An error occurred creating your profile.");
    } finally {
      setLoadingStep(false);
    }
  };

  const handleWorkspaceSubmit = async () => {
    setErrorMessage("");
    setLoadingStep(true);
    try {
      if (workspaceMode === "createNew") {
        if (!companyName) {
          throw new Error("Company Name is required.");
        }
        if (!phoneNumber.trim()) {
          throw new Error("Phone Number is required.");
        }
        if (!selectedApps.fuel && !selectedApps.flex && !selectedApps.trip) {
          throw new Error("Please select at least one application for your workspace.");
        }
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("Not authenticated");

        const token = await currentUser.getIdToken();

        const selectedTemplates = {
          captivaFuel: selectedApps.fuel
            ? [
                "capturedRefuel",
                "capturedTankLevelReading",
                "capturedFuelTankTransfer",
                "capturedBulkFuelReceived",
                "capturedFuelTankIssue",
              ]
            : [],
          captivaFlex: selectedApps.flex ? ["defaultFlexForm"] : [],
          captivaTrip: selectedApps.trip
            ? [
                "capturedTripPreInspection",
                "capturedTripDepart",
                "capturedTripCheckPoint",
                "capturedTripDelay",
                "capturedTripArrive",
                "capturedTripComplete",
              ]
            : [],
        };

        const payload = {
          name: companyName,
          city: town,
          province,
          street: addressLine1,
          addresLine1: addressLine1,
          country,
          postalCode,
          phoneNumber,
          owner: currentUser.uid,
          fuelUnitOfMeasure: "LITRES",
          selectedTemplates,
        };

        const baseUrl = process.env.REACT_APP_APP_ENV === 'local' 
          ? 'http://127.0.0.1:5001/twodata-dev/europe-west3/tenantapi'
          : 'https://europe-west3-twodata-dev.cloudfunctions.net/tenantapi';

        const resp = await fetch(
          `${baseUrl}/companies`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_X_API_KEY || "0f247291-b4bb-4895-91ac-ba26af7e55a2",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!resp.ok) {
          const body = await resp.json();
          throw new Error(body.message || "Failed to create company");
        }

        const company = await resp.json();
        if (typeof company?.loginNonce === "string" && company.loginNonce.trim()) {
          setLoginNonce(company.loginNonce.trim());
        }
        if (typeof currentUser.getIdToken === "function") {
          try {
            await currentUser.getIdToken(true);
          } catch {
            // Ignore token refresh failure; backend fallback verifies company ownership
          }
        }

        const draftId = queryDraftId || sanitizeDraftId(sessionStorage.getItem("captiva_onboarding_draft_id"));
        
        if (draftId && company.id) {
          const installDraftedModule = httpsCallable(functions, "installDraftedModule");
          const installResult = await installDraftedModule({
            claimToken: draftId,
            companyId: company.id
          });
          const installedConfig = (installResult.data as { moduleConfig?: DraftedModuleConfig })?.moduleConfig;
          setInstalledModule(installedConfig || null);
          if (sessionStorage.getItem("captiva_onboarding_draft_id")) {
            sessionStorage.removeItem("captiva_onboarding_draft_id");
          }

          const appList: string[] = [];
          if (installedConfig?.forms && installedConfig.forms.length > 0) appList.push("flex");
          if (installedConfig?.entities?.some((e: { kind?: string }) => e.kind === 'vehicle' || e.kind === 'tank' || e.kind === 'fuelType')) appList.push("fuel");
          if (installedConfig?.entities?.some((e: { kind?: string }) => e.kind === 'vehicle' || e.kind === 'vehicleGroup')) appList.push("trip");
          const appsQuery = appList.length > 0 ? `&apps=${encodeURIComponent(appList.join(","))}` : "";

          window.location.href = `/build/onboard/success?token=${encodeURIComponent(draftId)}${appsQuery}`;
          return;
        } else if (isClaiming && company.id) {
          const q = query(collection(db, "flexModuleDrafts"), where("uid", "==", currentUser.uid), where("status", "==", "pending_claim"));
          const draftDocs = await getDocs(q);
          
          if (!draftDocs.empty) {
            const claimToken = draftDocs.docs[0].id;
            const installDraftedModule = httpsCallable(functions, "installDraftedModule");
            const installResult = await installDraftedModule({
              claimToken,
              companyId: company.id
            });
            const installedConfig = (installResult.data as { moduleConfig?: DraftedModuleConfig })?.moduleConfig;
            setInstalledModule(installedConfig || null);

            const appList: string[] = [];
            if (installedConfig?.forms && installedConfig.forms.length > 0) appList.push("flex");
            if (installedConfig?.entities?.some((e: { kind?: string }) => e.kind === 'vehicle' || e.kind === 'tank' || e.kind === 'fuelType')) appList.push("fuel");
            if (installedConfig?.entities?.some((e: { kind?: string }) => e.kind === 'vehicle' || e.kind === 'vehicleGroup')) appList.push("trip");
            const appsQuery = appList.length > 0 ? `&apps=${encodeURIComponent(appList.join(","))}` : "";

            window.location.href = `/build/onboard/success?token=${encodeURIComponent(claimToken)}${appsQuery}`;
            return;
          }
        }
        
        setActive(4);
      } else {
        if (!activationCode) {
          throw new Error("Activation Code is required.");
        }
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("Not authenticated");
        
        const token = await currentUser.getIdToken();
        const payload = {
          activationCode,
          uid: currentUser.uid,
        };

        const baseUrl = process.env.REACT_APP_APP_ENV === 'local' 
          ? 'http://127.0.0.1:5001/twodata-dev/europe-west3/tenantapi'
          : 'https://europe-west3-twodata-dev.cloudfunctions.net/tenantapi';

        const resp = await fetch(
          `${baseUrl}/activate`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_X_API_KEY || "0f247291-b4bb-4895-91ac-ba26af7e55a2",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!resp.ok) {
          const body = await resp.json();
          throw new Error(body.message || "Failed to join company");
        }

        const activateData = await resp.json().catch(() => ({}));
        if (typeof activateData?.loginNonce === "string" && activateData.loginNonce.trim()) {
          setLoginNonce(activateData.loginNonce.trim());
        }

        setActive(4);
      }
    } catch (err: any) {
      setErrorMessage(err.message || "An error occurred.");
    } finally {
      setLoadingStep(false);
    }
  };

  const handleFinish = async () => {
    setLoginNonce(null);
    // Force refresh token so the new companyId custom claim is pulled from the backend
    if (auth.currentUser) {
      await auth.currentUser.getIdToken(true);
    }
    // Navigate to dashboard overview and force reload to refresh token claims
    window.location.href = "/dashboard/overview";
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8f9fa" }}>
        <Loader color="cap-navy" />
      </Box>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        paddingTop: 24,
        paddingBottom: 64,
        paddingLeft: 16,
        paddingRight: 16,
        backgroundColor: "#f8f9fa",
        [theme.fn.largerThan("sm")]: {
          paddingTop: 48,
          paddingBottom: 80,
          paddingLeft: 24,
          paddingRight: 24,
        },
      })}
    >
      <Box sx={{ width: "100%", maxWidth: 640 }}>
        <Stack spacing="lg" sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", justifyContent: "center", paddingBottom: 4 }}>
            <img
              src="/captiva-logo.png"
              alt="Captiva"
              style={{ height: 34, width: "auto", objectFit: "contain" }}
            />
          </Box>
          
          <Paper
            shadow="sm"
            radius="md"
            p="xl"
            withBorder
            sx={(theme) => ({
              width: "100%",
              backgroundColor: "#ffffff",
              [theme.fn.smallerThan("sm")]: {
                padding: theme.spacing.md,
              },
            })}
          >
            <Group position="apart" align="center" mb="xs">
              <Title order={2} color="dark">
                {isClaiming ? "Claim your Workspace" : "Register"}
              </Title>
            </Group>
            
            {isClaiming && active === 0 && (
              <Text color="dimmed" size="sm" mb="md">
                Create an account to lock in your AI-generated module and access your dashboard.
              </Text>
            )}

            {errorMessage && (
              <Notification icon={<InfoCircle size={18} />} color="red" onClose={() => setErrorMessage("")} mb="xl">
                {errorMessage}
              </Notification>
            )}

            <Stepper
              active={active}
              size="sm"
              mb="xl"
              styles={(theme) => ({
                root: {
                  width: "100%",
                },
                steps: {
                  flexWrap: "nowrap",
                },
                step: {
                  padding: 0,
                  cursor: "default",
                },
                stepIcon: {
                  [theme.fn.smallerThan("sm")]: {
                    minWidth: 28,
                    width: 28,
                    height: 28,
                    fontSize: theme.fontSizes.xs,
                  },
                },
                stepBody: {
                  [theme.fn.smallerThan("xs")]: {
                    marginLeft: 6,
                  },
                },
                stepDescription: {
                  [theme.fn.smallerThan("sm")]: {
                    display: "none",
                  },
                },
                stepLabel: {
                  [theme.fn.smallerThan("xs")]: {
                    display: "none",
                  },
                },
                separator: {
                  [theme.fn.smallerThan("sm")]: {
                    marginLeft: 4,
                    marginRight: 4,
                  },
                },
              })}
            >
              <Stepper.Step label="Account" description="Create login" />
              <Stepper.Step label="Verify" description="Check email" />
              <Stepper.Step label="Profile" description="Personal info" />
              <Stepper.Step label="Workspace" description="Setup company" />
            </Stepper>

            <Divider mb="xl" />

            {active === 0 && (
              <Stack spacing="md">
                <Title order={4}>Account Details</Title>
                <TextInput
                  required
                  size="md"
                  type="email"
                  autoComplete="email"
                  label="Email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <PasswordInput
                  required
                  size="md"
                  autoComplete="new-password"
                  label="Password"
                  placeholder="Password (min 8 chars)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <PasswordInput
                  required
                  size="md"
                  autoComplete="new-password"
                  label="Retype password"
                  placeholder="Retype password"
                  value={passwordCheck}
                  onChange={(e) => setPasswordCheck(e.target.value)}
                />
                <Checkbox
                  required
                  size="md"
                  color="cap-navy"
                  label="I agree to the Terms & Conditions"
                  checked={termsAccepted}
                  onChange={(event) => setTermsAccepted(event.currentTarget.checked)}
                  mt="xs"
                />
                <Button
                  color="cap-navy"
                  fullWidth
                  mt="md"
                  size="md"
                  disabled={!email || !password || !passwordCheck || !termsAccepted || loadingStep}
                  onClick={handleAccountCreation}
                >
                  {loadingStep ? <Loader size="sm" color="white" /> : "Create Account"}
                </Button>
              </Stack>
            )}

          {active === 1 && (
            <Stack spacing="md">
              <Title order={4}>Verify Email</Title>
              <Text size="md">Check your email inbox for a verification link.</Text>
              <Text size="sm" color="dimmed">Sent from admin@captivadata.com.</Text>
              <Button
                color="cap-navy"
                fullWidth
                mt="md"
                size="md"
                disabled={loadingStep}
                onClick={handleCheckEmailVerified}
              >
                {loadingStep ? <Loader size="sm" color="white" /> : "Continue"}
              </Button>
              <Button
                variant="subtle"
                color="gray"
                fullWidth
                size="sm"
                mt="xs"
                onClick={handleUseDifferentEmail}
              >
                Use a different email
              </Button>
            </Stack>
          )}

          {active === 2 && (
            <Stack spacing="md">
              <Title order={4}>Your Profile</Title>
              <SimpleGrid cols={2} spacing="sm" breakpoints={[{ maxWidth: "xs", cols: 1 }]}>
                <TextInput
                  required
                  size="md"
                  autoComplete="given-name"
                  label="First Name"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <TextInput
                  required
                  size="md"
                  autoComplete="family-name"
                  label="Last Name"
                  placeholder="Smith"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </SimpleGrid>
              <Button
                color="cap-navy"
                fullWidth
                mt="md"
                size="md"
                disabled={!firstName || !lastName}
                onClick={handleProfileSubmit}
              >
                Continue
              </Button>
              <Button
                variant="subtle"
                color="gray"
                fullWidth
                size="sm"
                mt="xs"
                onClick={handleUseDifferentEmail}
              >
                Use a different email
              </Button>
            </Stack>
          )}

          {active === 3 && (
            <Stack spacing="md">
              <Title order={4}>Workspace Setup</Title>
              {!isClaiming && (
                <RadioGroup
                  value={workspaceMode}
                  onChange={setWorkspaceMode}
                  name="workspaceMode"
                  label="Do you want to create a new company or join an existing one?"
                  required
                  mb="md"
                  size="md"
                >
                  <Radio value="createNew" label="Create new company" color="cap-navy" />
                  <Radio value="joinExisting" label="Join existing company" color="cap-navy" />
                </RadioGroup>
              )}

              {workspaceMode === "createNew" ? (
                <Box>
                  <TextInput
                    required
                    size="md"
                    autoComplete="organization"
                    label="Company Name"
                    placeholder="Acme Corp"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    mb="sm"
                  />

                  <Box mb="md">
                    <Text weight={500} size="sm" mb="xs">
                      Select Applications for your Workspace:
                    </Text>
                    <Stack spacing="xs">
                      <Paper p="xs" withBorder radius="md">
                        <Checkbox
                          size="md"
                          color="cap-navy"
                          label="Captiva Fuel (Bowsers, Tanks, Refuels)"
                          checked={selectedApps.fuel}
                          onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            setSelectedApps((prev) => ({ ...prev, fuel: checked }));
                          }}
                        />
                      </Paper>
                      <Paper p="xs" withBorder radius="md">
                        <Checkbox
                          size="md"
                          color="cap-navy"
                          label="Captiva Flex (General Purpose Data Capture, Checklists, Timesheets)"
                          checked={selectedApps.flex}
                          onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            setSelectedApps((prev) => ({ ...prev, flex: checked }));
                          }}
                        />
                      </Paper>
                      <Paper p="xs" withBorder radius="md">
                        <Checkbox
                          size="md"
                          color="cap-navy"
                          label="Captiva Trip (GPS Route Tracking, Checkpoints, Driver Inspections)"
                          checked={selectedApps.trip}
                          onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            setSelectedApps((prev) => ({ ...prev, trip: checked }));
                          }}
                        />
                      </Paper>
                    </Stack>
                  </Box>

                  <TextInput
                    required
                    size="md"
                    type="tel"
                    autoComplete="tel"
                    label="Phone / WhatsApp Number"
                    placeholder="083 123 4567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    mb="sm"
                  />
                  <TextInput
                    size="md"
                    autoComplete="street-address"
                    label="Address Line 1"
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    mb="sm"
                  />
                  <SimpleGrid cols={2} spacing="sm" mb="sm" breakpoints={[{ maxWidth: "xs", cols: 1 }]}>
                    <TextInput
                      size="md"
                      autoComplete="address-level2"
                      label="Town / City"
                      value={town}
                      onChange={(e) => setTown(e.target.value)}
                    />
                    <TextInput
                      size="md"
                      autoComplete="address-level1"
                      label="Province / State"
                      value={province}
                      onChange={(e) => setProvince(e.target.value)}
                    />
                  </SimpleGrid>
                  <SimpleGrid cols={2} spacing="sm" breakpoints={[{ maxWidth: "xs", cols: 1 }]}>
                    <TextInput
                      size="md"
                      autoComplete="country-name"
                      label="Country"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                    />
                    <TextInput
                      size="md"
                      autoComplete="postal-code"
                      label="Postal Code"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                    />
                  </SimpleGrid>
                </Box>
              ) : (
                <Box>
                  <Text size="sm" color="dimmed" mb="xs">Find the code in "Settings" / "Company Details" of an admin user's app.</Text>
                  <TextInput
                    required
                    size="md"
                    label="Activation Code"
                    placeholder="4JXE242A"
                    value={activationCode}
                    onChange={(e) => setActivationCode(e.target.value)}
                  />
                </Box>
              )}

              <Button
                color="cap-navy"
                fullWidth
                mt="xl"
                size="md"
                disabled={
                  loadingStep ||
                  (workspaceMode === "createNew"
                    ? !companyName || !phoneNumber.trim() || (!selectedApps.fuel && !selectedApps.flex && !selectedApps.trip)
                    : !activationCode)
                }
                onClick={handleWorkspaceSubmit}
              >
                {loadingStep ? <Loader size="sm" color="white" /> : "Complete Setup"}
              </Button>
              <Button
                variant="subtle"
                color="gray"
                fullWidth
                size="sm"
                mt="xs"
                onClick={handleUseDifferentEmail}
              >
                Use a different email
              </Button>
            </Stack>
          )}

          {active === 4 && (() => {
            const platform = detectRegistrationPlatform();
            const isMobile = platform === "ios" || platform === "android";

            const enabledApps: CaptivaAppKey[] = [];
            if (workspaceMode === "joinExisting") {
              if (sourceApp) {
                enabledApps.push(sourceApp);
              } else {
                enabledApps.push("fuel", "flex", "trip");
              }
            } else {
              const isFuelEnabled = Boolean(
                selectedApps.fuel ||
                  sourceApp === "fuel" ||
                  installedModule?.entities?.some(
                    (e: any) => e.kind === "vehicle" || e.kind === "tank" || e.kind === "fuelType"
                  )
              );
              const isFlexEnabled = Boolean(
                selectedApps.flex ||
                  sourceApp === "flex" ||
                  (installedModule?.forms && installedModule.forms.length > 0)
              );
              const isTripEnabled = Boolean(
                selectedApps.trip ||
                  sourceApp === "trip" ||
                  installedModule?.entities?.some(
                    (e: any) => e.kind === "vehicle" || e.kind === "vehicleGroup"
                  )
              );

              if (sourceApp) {
                enabledApps.push(sourceApp);
              }
              if (isFuelEnabled && !enabledApps.includes("fuel")) enabledApps.push("fuel");
              if (isFlexEnabled && !enabledApps.includes("flex")) enabledApps.push("flex");
              if (isTripEnabled && !enabledApps.includes("trip")) enabledApps.push("trip");
            }

            return (
              <Stack spacing="lg">
                <Box>
                  <Title order={3} color="green">Account successfully created!</Title>
                  <Text size="md" mt="sm">
                    {isMobile
                      ? "Your workspace is ready. Continue to your mobile app to sign in, or open the web dashboard."
                      : "Your workspace is ready. Let's head over to your dashboard."}
                  </Text>
                </Box>

                {enabledApps.length > 0 && (
                  <Paper p="md" withBorder sx={{ backgroundColor: "#f8f9fa" }} radius="md">
                    <Text weight={600} mb="lg" size="lg">
                      {isMobile ? "Continue in your App" : "Download your Apps"}
                    </Text>
                    <Stack spacing="xl">
                      {enabledApps.map((appKey) => {
                        const appConfig = REGISTRATION_APPS[appKey];
                        return (
                          <AppDownloads
                            key={appKey}
                            appKey={appKey}
                            appName={appConfig.label}
                            iosUrl={appConfig.iosUrl}
                            androidUrl={appConfig.androidUrl}
                            loginNonce={loginNonce}
                            isSourceApp={sourceApp === appKey}
                          />
                        );
                      })}
                    </Stack>
                  </Paper>
                )}

                <Button
                  color="cap-navy"
                  variant={isMobile ? "outline" : "filled"}
                  mt="xl"
                  size="md"
                  onClick={handleFinish}
                  fullWidth
                >
                  {isMobile ? "Open Web Dashboard" : "Go to Dashboard"}
                </Button>
              </Stack>
            );
          })()}
        </Paper>
        <Group position="center" pb="md">
          <Button
            variant="subtle"
            size="xs"
            color="gray"
            onClick={() => setHelpModalOpened(true)}
          >
            Need Onboarding Help?
          </Button>
        </Group>
      </Stack>
    </Box>
    <RequestOnboardingHelpModal
      opened={helpModalOpened}
      onClose={() => setHelpModalOpened(false)}
      source="registration_flow"
      defaultName={`${firstName} ${lastName}`.trim()}
      defaultEmail={email}
      defaultPhone={phoneNumber}
      companyName={companyName}
      draftId={queryDraftId}
    />
  </Box>
);
}

export function detectRegistrationPlatform(): "ios" | "android" | "desktop" {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  return "desktop";
}

function AppDownloads({
  appKey,
  appName,
  iosUrl,
  androidUrl,
  loginNonce,
  isSourceApp = false,
}: {
  appKey: CaptivaAppKey;
  appName: string;
  iosUrl: string;
  androidUrl: string;
  loginNonce?: string | null;
  isSourceApp?: boolean;
}) {
  const platform = detectRegistrationPlatform();

  if (platform === "ios" || platform === "android") {
    const storeUrl = platform === "ios" ? iosUrl : androidUrl;
    const storeLabel = platform === "ios" ? "Download on the App Store" : "Get it on Google Play";
    const continueUrl = buildMobileAppContinueUrl(appKey, loginNonce);

    return (
      <Paper p="sm" withBorder radius="md" sx={{ backgroundColor: "#ffffff" }}>
        <Stack spacing="xs">
          <Text size="md" weight={600} color="dark">
            {appName}
          </Text>
          <Button
            component="a"
            href={continueUrl}
            rel="noreferrer noopener"
            color="cap-navy"
            size="md"
            fullWidth
          >
            Continue to {appName}
          </Button>
          {!isSourceApp && (
            <Text
              component="a"
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="xs"
              color="dimmed"
              align="center"
              sx={{ textDecoration: "underline" }}
            >
              Not installed yet? {storeLabel}
            </Text>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <Box>
      <Text size="md" weight={600} mb="sm" color="dark">{appName}</Text>
      <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: "xs", cols: 1 }]}>
        <Paper p="sm" withBorder radius="md">
          <Stack align="center" spacing="xs">
            <QRImage url={iosUrl} />
            <Button
              component="a"
              href={iosUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="light"
              color="cap-navy"
              size="sm"
              fullWidth
            >
              iOS (App Store)
            </Button>
          </Stack>
        </Paper>
        <Paper p="sm" withBorder radius="md">
          <Stack align="center" spacing="xs">
            <QRImage url={androidUrl} />
            <Button
              component="a"
              href={androidUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="light"
              color="cap-navy"
              size="sm"
              fullWidth
            >
              Android (Google Play)
            </Button>
          </Stack>
        </Paper>
      </SimpleGrid>
    </Box>
  );
}

function QRImage({ url }: { url: string }) {
  const [dataUrl, setDataUrl] = useState("");
  useEffect(() => {
    try {
      const res = QRCode.toDataURL(url, { margin: 1, width: 100 });
      if (res && typeof res.then === "function") {
        res.then(setDataUrl).catch(console.error);
      }
    } catch (err) {
      console.error(err);
    }
  }, [url]);
  return dataUrl ? <Image src={dataUrl} width={100} height={100} alt="QR Code" /> : <Loader size="sm" />;
}
